import Highcharts from 'highcharts';
import HighStock from 'highcharts/highstock';
import type HighchartsReact from 'highcharts-react-official';

// Hicharts Accessibility
import highchartsAccessibility from 'highcharts/modules/accessibility';

highchartsAccessibility(Highcharts);
highchartsAccessibility(HighStock);

/**
 * Highcharts doesn't compute chart widths properly when combined with CSS's transform property in our current version of the plugin
 * (Highcharts v7.1.1). The issue got fixed in the 7.1.3 release, but it also apparently causes breaking changes for us since some
 * of the type definitions became stricter.
 *
 * Vague TODO - Upgrade Highcharts version, get rid of this fix and resolve all Typescript issues related.
 *
 * For more information on the issue: https://github.com/highcharts/highcharts/issues/10498
 */
const init = () => {
  for (const H of [Highcharts, HighStock] as any[]) {
    const { win } = H;

    H.getStyle = (el: HTMLElement, prop: string, toInt: Function) => {
      let style;

      // For width and height, return the actual inner pixel size
      if (prop === 'width') {
        let offsetWidth = Math.min(el.offsetWidth, el.scrollWidth);

        // In flex boxes, we need to use getBoundingClientRect and floor it,
        // because scrollWidth doesn't support subpixel precision...
        const boundingClientRectWidth = el.getBoundingClientRect && el.getBoundingClientRect().width;

        // ...unless if the containing div or its parents are transform-scaled
        // down, in which case the boundingClientRect can't be used as it is
        // also scaled down.
        if (boundingClientRectWidth < offsetWidth && boundingClientRectWidth >= offsetWidth - 1) {
          offsetWidth = Math.floor(boundingClientRectWidth);
        }

        return Math.max(0, offsetWidth - H.getStyle(el, 'padding-left') - H.getStyle(el, 'padding-right'));
      }

      if (prop === 'height') {
        return Math.max(0, Math.min(el.offsetHeight, el.scrollHeight) - H.getStyle(el, 'padding-top') - H.getStyle(el, 'padding-bottom'));
      }

      if (!win.getComputedStyle) {
        // SVG not supported, forgot to load oldie.js?
        H.error(27, true);
      }

      // Otherwise, get the computed style
      style = win.getComputedStyle(el, undefined); // eslint-disable-line no-undefined

      if (style) {
        style = style.getPropertyValue(prop);
        if (H.pick(toInt, prop !== 'opacity')) {
          style = H.pInt(style);
        }
      }

      return style;
    };

    // Fixes an issue with the tooltips being misaligned when transform: scale() is applied on the chart.
    // Reference: https://github.com/highcharts/highcharts/issues/2405#issuecomment-132638603
    H.wrap(
      H.Pointer.prototype,
      'normalize',
      function (
        this: HighchartsReact,
        proceed: Highcharts.WrapProceedFunction,
        event: MouseEvent | PointerEvent | TouchEvent,
        chartPosition: Highcharts.OffsetObject,
      ) {
        const e = proceed.call(this, event, chartPosition);
        const element = this.chart.container;
        if (element && element.offsetWidth && element.offsetHeight) {
          const scaleX = element.getBoundingClientRect().width / element.offsetWidth;
          const scaleY = element.getBoundingClientRect().height / element.offsetHeight;
          if (scaleX !== 1) {
            e.chartX = Number(e.chartX / scaleX);
          }
          if (scaleY !== 1) {
            e.chartY = Number(e.chartY / scaleY);
          }
        }
        return e;
      },
    );
  }
};

export default {
  init,
};
